import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/core';
import { token } from '@atlaskit/tokens';

type Props = {
  isSidebarOpen: boolean;
  width: string | number;
  sideBarDistanceFromTop?: string;
};

const renderSidebarWidth = (props: Props) => (props.isSidebarOpen ? props.width : '0');

const commonSidebarStyles = css`
  border-radius: ${token('space.100')};
  margin: ${token('space.050')} !important; // !important is for change payment method sidebar overrides
`;

const opacityChange = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const renderSideBarTop = (props: Props) =>
  props.sideBarDistanceFromTop ? props.sideBarDistanceFromTop : 'var(--topNavigationHeight)';

export const SidebarContainer = styled.div<Props>`
  position: fixed;
  right: 0;
  top: ${renderSideBarTop};
  bottom: 0;
  box-shadow: ${token('elevation.shadow.overlay')};
  background: ${token('elevation.surface.overlay')};
  z-index: 502;
  overflow-y: auto;
  width: ${renderSidebarWidth};
  transition: width 0.3s ease;
  ${commonSidebarStyles}
  .plan-select-item {
    // Override the z-index so that footer is not overlapped by the select element
    & > div {
      z-index: initial;
    }
  }
  footer {
    // Temporary fix for submit & cancel CTAs to match the new design as Atlaskit is not exposing the same styles.
    // !important had to be added as well. PaneContainer -> '> div:nth-child(2)' is part of this fix.
    background: ${token('elevation.surface')};
    justify-content: space-between;
    // !important in padding is for change payment method sidebar overrides
    padding: ${token('space.250')} ${token('space.400')} ${token('space.400')} !important;
    position: fixed !important;
    right: 0 !important;
    bottom: 0 !important;
    box-sizing: border-box;
    width: ${renderSidebarWidth};
    ${commonSidebarStyles}
    animation: ${opacityChange} 0.5s; // Needed so that the footer does not appear suddenly
    > div {
      gap: ${token('space.100')};
      width: calc(100% - ${token('space.100')}); // 100% - gap
      > div {
        // Button container
        flex-basis: 50%;
        :before {
          display: none !important; // Remove the unwanted space coming before save button
        }
      }
    }
    button {
      width: 100%;
      margin-left: ${token('space.100')}; // Few sidebar footers have different margin-left. Override it here
      font-size: 14px; // Make font size consistent so that the height of the button is consistent
      margin-bottom: 0 !important; // Remove any bottom spacing in the buttons (came in change bill date sidebar)
      &:first-child {
        margin-left: 0; // Remove any left spacing in the first button
      }
      &.cancel-view-pricing {
        // Target: packages/components/base/common-sidebars/src/view-pricing/src/view-pricing.tsx
        margin-left: auto;
        width: unset;
      }
    }
  }
`;

export const PaneContainer = styled.div`
  // Bottom padding is handled in div:nth-child(2) while fixed footer has token('space.400')
  padding: ${token('space.400')} ${token('space.400')} 0;
  position: relative;
  height: 100%;
  .pane-sidebar-content {
    padding: 0;
  }
  > div:nth-child(2),
  > form:nth-child(2) {
    // Targets pane-sidebar-content. However, in few sidebars the class is missing or has form, hence using second child.
    // This will also introduce extra bottom padding in Pane not setup info panel, but it's fine. B)
    padding-bottom: 84px !important; // Padding-bottom is given as the footer is fixed and takes up 84px
    height: auto; // Handles bottom spacing for sidebars such as add and edit payment method
  }
`;

export const FixedCloseButton = styled.div`
  position: absolute;
  right: 32px;
  top: 40px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 8px;
  background: ${token('color.background.accent.gray.subtlest')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CloseButton = styled.div`
  min-width: 24px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 8px;
  background: ${token('color.background.accent.gray.subtlest')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SidebarTitle = styled.h2`
  font-weight: 500;
`;
